const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const PNG_UNIT_METERS = 1;
const PIXELS_PER_METER_PER_DPI = 39.37007874;

let crc32Table = null;

const getCrc32Table = () => {
  if (crc32Table) {
    return crc32Table;
  }
  crc32Table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = (c & 1) !== 0 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    crc32Table[i] = c >>> 0;
  }
  return crc32Table;
};

const crc32 = (bytes) => {
  const table = getCrc32Table();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const readU32 = (view, offset) => view.getUint32(offset, false);

const writeU32 = (view, offset, value) => {
  view.setUint32(offset, value >>> 0, false);
};

const isPngSignature = (bytes) => {
  if (!(bytes instanceof Uint8Array) || bytes.length < PNG_SIGNATURE.length) {
    return false;
  }
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) {
      return false;
    }
  }
  return true;
};

const chunkTypeToString = (bytes, offset) =>
  String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);

const createChunk = (chunkType, data) => {
  const chunkData = data instanceof Uint8Array ? data : new Uint8Array(data || []);
  const out = new Uint8Array(12 + chunkData.length);
  const view = new DataView(out.buffer);

  writeU32(view, 0, chunkData.length);
  out[4] = chunkType.charCodeAt(0);
  out[5] = chunkType.charCodeAt(1);
  out[6] = chunkType.charCodeAt(2);
  out[7] = chunkType.charCodeAt(3);
  out.set(chunkData, 8);

  const crc = crc32(out.subarray(4, 8 + chunkData.length));
  writeU32(view, 8 + chunkData.length, crc);
  return out;
};

const createPhysChunk = (dpi) => {
  const safeDpi = Number.isFinite(Number(dpi)) && Number(dpi) > 0 ? Number(dpi) : 600;
  const pixelsPerMeter = Math.max(1, Math.round(safeDpi * PIXELS_PER_METER_PER_DPI));
  const data = new Uint8Array(9);
  const view = new DataView(data.buffer);
  writeU32(view, 0, pixelsPerMeter);
  writeU32(view, 4, pixelsPerMeter);
  data[8] = PNG_UNIT_METERS;
  return createChunk('pHYs', data);
};

const parseChunks = (bytes) => {
  const chunks = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = PNG_SIGNATURE.length;

  while (offset + 12 <= bytes.length) {
    const length = readU32(view, offset);
    const chunkEnd = offset + 12 + length;
    if (chunkEnd > bytes.length) {
      throw new Error('Invalid PNG chunk length.');
    }
    const type = chunkTypeToString(bytes, offset + 4);
    chunks.push({
      type,
      start: offset,
      end: chunkEnd,
    });
    offset = chunkEnd;
    if (type === 'IEND') {
      break;
    }
  }

  if (!chunks.length || chunks[0].type !== 'IHDR') {
    throw new Error('Invalid PNG structure.');
  }

  return chunks;
};

const blobToArrayBuffer = async (blob) => {
  if (blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read PNG blob.'));
    reader.readAsArrayBuffer(blob);
  });
};

export const embedPngDpi = async (blob, dpi) => {
  if (!blob) {
    return {
      blob,
      didEmbed: false,
      warning: 'No export image available for DPI metadata.',
    };
  }

  try {
    const buffer = await blobToArrayBuffer(blob);
    const bytes = new Uint8Array(buffer);
    if (!isPngSignature(bytes)) {
      throw new Error('Input is not a PNG image.');
    }

    const chunks = parseChunks(bytes);
    const physChunk = createPhysChunk(dpi);
    const parts = [PNG_SIGNATURE];
    let insertedPhys = false;

    for (const chunk of chunks) {
      if (chunk.type === 'pHYs') {
        continue;
      }
      parts.push(bytes.subarray(chunk.start, chunk.end));
      if (!insertedPhys && chunk.type === 'IHDR') {
        parts.push(physChunk);
        insertedPhys = true;
      }
    }

    if (!insertedPhys) {
      throw new Error('PNG IHDR chunk not found.');
    }

    let totalLength = 0;
    for (const part of parts) {
      totalLength += part.length;
    }
    const out = new Uint8Array(totalLength);
    let cursor = 0;
    for (const part of parts) {
      out.set(part, cursor);
      cursor += part.length;
    }

    return {
      blob: new Blob([out], { type: 'image/png' }),
      didEmbed: true,
      warning: '',
    };
  } catch (error) {
    return {
      blob,
      didEmbed: false,
      warning: 'Could not embed PNG DPI metadata; export saved without explicit DPI tag.',
    };
  }
};

const pngDpi = {
  embedPngDpi,
};

export default pngDpi;
